import React from 'react';
import { Card, Row, Col, Typography, Button, Space, Steps } from 'antd';
import { 
  CalculatorOutlined, 
  HistoryOutlined, 
  BookOutlined, 
  ThunderboltOutlined,
  AimOutlined,
  DeploymentUnitOutlined,
  ForkOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/i18n';
import './HomePage.less';
// 集中来源：保护类型与名称映射
import { PROTECTION_TYPES } from '../utils/protectionTypes';
import { getProtectionTypeName } from '../utils/exportUtils';

const { Title, Paragraph } = Typography;

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  
  const features = [
    {
      icon: <CalculatorOutlined />,
      title: '专业计算',
      description: '基于IEC 60255标准的继电保护定值计算'
    },
    {
      icon: <HistoryOutlined />,
      title: '历史记录',
      description: '保存所有计算历史，方便追溯和对比'
    },
    {
      icon: <BookOutlined />,
      title: '知识库',
      description: '丰富的继电保护原理和应用指南'
    }
  ];

  // 统一图标与简要描述
  const ICON_MAP = {
    overcurrent: ThunderboltOutlined,
    distance: AimOutlined,
    differential: DeploymentUnitOutlined,
    directional: ForkOutlined,
    zero_sequence: ThunderboltOutlined,
    longitudinal: SafetyCertificateOutlined
  };
  const TYPE_DESC = {
    overcurrent: '最常见的保护类型，适用于配电线路',
    distance: '基于阻抗测量，适用于输电线路',
    differential: '基于电流差值，适用于重要设备',
    directional: '判断故障方向，确保选择性',
    zero_sequence: '接地故障检测（零序电流/功率）',
    longitudinal: '线路两端比对，实现快速选择性跳闸'
  };

  const protectionTypes = PROTECTION_TYPES.map((value) => {
    const IconComp = ICON_MAP[value] || SafetyCertificateOutlined;
    return {
      icon: <IconComp />,
      title: getProtectionTypeName(value, { t }),
      description: TYPE_DESC[value] || ''
    };
  });

  return (
    <div className="home-page">
      {/* Banner 区域 */}
      <div className="banner">
        <div className="banner-content">
          <Title level={1} style={{ color: '#fff', marginBottom: 16 }}>
            电力继电保护计算平台
          </Title>
          <Paragraph style={{ color: '#fff', fontSize: 18, maxWidth: 800 }}>
            基于IEC 60255标准的专业继电保护定值计算工具，为电力系统工程师提供准确、可靠的计算服务
          </Paragraph>
          <Button 
            type="primary" 
            size="large" 
            onClick={() => navigate('/calculation')}
            style={{ marginTop: 24 }}
            aria-label={t('onboarding.cta.start')}
          >
            立即计算
          </Button>
        </div>
      </div>

      {/* 首次使用引导 */}
      <div className="onboarding" aria-label={t('onboarding.title')} role="region" style={{ marginTop: 24 }}>
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={3} style={{ marginBottom: 8 }}>{t('onboarding.title')}</Title>
              <Paragraph type="secondary" style={{ marginBottom: 16 }}>{t('onboarding.desc')}</Paragraph>
            </div>
            <Space>
              <Button type="primary" onClick={() => navigate('/calculation')} aria-label={t('onboarding.cta.start')}>{t('onboarding.cta.start')}</Button>
              <Button onClick={() => navigate('/history')} aria-label={t('onboarding.cta.history')}>{t('onboarding.cta.history')}</Button>
            </Space>
          </div>
          <Steps direction="vertical" style={{ marginTop: 16 }} items={[
            { title: t('onboarding.step.template'), description: '选择常用参数模板快速开始' },
            { title: t('onboarding.step.calculate'), description: '填写必要参数并查看计算结果' },
            { title: t('onboarding.step.save'), description: '保存结果到历史记录，便于对比与追溯' },
            { title: t('onboarding.step.overlay'), description: '在计算页叠加曲线，进行选择性校核' },
            { title: t('onboarding.step.export'), description: '导出叠加视图或报告，支持 PNG/Markdown/PDF' },
          ]} />
        </Card>
      </div>

      {/* 特色功能区域 */}
      <div className="features">
        <div className="section-header">
          <Title level={2} style={{ textAlign: 'center', marginBottom: 48 }}>
            平台特色
          </Title>
        </div>
        
        <Row gutter={[24, 24]} justify="center">
          {features.map((feature, index) => (
            <Col xs={24} sm={12} md={8} key={index}>
              <Card className="feature-card" hoverable>
                <div className="feature-icon">{feature.icon}</div>
                <Title level={4}>{feature.title}</Title>
                <Paragraph type="secondary">{feature.description}</Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* 保护类型区域 */}
      <div className="protection-types">
        <div className="section-header">
          <Title level={2} style={{ textAlign: 'center', marginBottom: 8 }}>
            支持的保护类型
          </Title>
          <Paragraph style={{ textAlign: 'center', fontSize: 16 }}>
            覆盖电力系统主要保护类型，满足不同应用场景需求
          </Paragraph>
        </div>
        
        <Row gutter={[16, 16]} style={{ marginTop: 32 }}>
          {protectionTypes.map((type, index) => (
            <Col xs={12} sm={12} md={6} key={index}>
              <Card className="type-card" hoverable>
                <div className="type-icon">{type.icon}</div>
                <Title level={5}>{type.title}</Title>
                <Paragraph type="secondary" style={{ fontSize: 12 }}>
                  {type.description}
                </Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* 平台介绍区域 */}
      <div className="platform-intro">
        <Row gutter={[24, 24]} align="middle">
          <Col xs={24} lg={12}>
            <div className="intro-content">
              <Title level={2}>专业可靠的继电保护计算工具</Title>
              <Paragraph style={{ fontSize: 16, lineHeight: 1.8 }}>
                本平台专为电力系统工程师设计，提供基于国际标准的继电保护定值计算服务。
                通过精确的算法和丰富的工程经验，帮助用户快速获得准确的保护定值。
              </Paragraph>
              <div className="intro-features">
                <Space direction="vertical">
                  <Paragraph>
                    <strong>• 标准化算法：</strong>严格遵循IEC 60255国际标准
                  </Paragraph>
                  <Paragraph>
                    <strong>• 多种保护类型：</strong>支持过电流、距离、差动等多种保护计算
                  </Paragraph>
                  <Paragraph>
                    <strong>• 工程实用性：</strong>结合实际工程经验优化算法
                  </Paragraph>
                  <Paragraph>
                    <strong>• 数据安全管理：</strong>所有计算数据加密存储
                  </Paragraph>
                </Space>
              </div>
              <Button 
                type="primary" 
                size="large" 
                onClick={() => navigate('/calculation')}
                style={{ marginTop: 24 }}
              >
                开始计算
              </Button>
            </div>
          </Col>
          <Col xs={24} lg={12}>
            <div className="intro-image">
              <div className="image-placeholder">
                <CalculatorOutlined style={{ fontSize: 48, color: '#1890FF' }} />
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* CTA区域 */}
      <div className="cta-section">
        <Card className="cta-card">
          <div className="cta-content">
            <Title level={3} style={{ color: '#fff' }}>立即体验专业继电保护计算</Title>
            <Paragraph style={{ color: '#fff', fontSize: 16, marginBottom: 24 }}>
              保护计算支持过电流、距离、差动等多种保护类型的定值计算，基于行业标准算法。
            </Paragraph>
            <Button 
              type="primary" 
              size="large" 
              onClick={() => navigate('/calculation')}
              ghost
            >
              进入保护计算
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default HomePage;
